/* Erwal — zachte navigatie tussen collecties en een zwevende filterknop.
 *
 * 1. Zachte navigatie (wens klant, 5 sep 2026): een andere categorie
 *    kiezen (de pillen boven het raster, de categorielijst in de zijbalk)
 *    is in Shopify een andere pagina, dus een volledige herlaad. Dat voelt
 *    hard. Hier halen we de nieuwe pagina op de achtergrond op, wisselen
 *    de inhoud van <main> (kop, deal, pillen, raster, filters) met een
 *    korte vervaging, zetten de URL en de titel goed en houden de
 *    scrollpositie vast. Filters zelf gingen al via ajax (Horizon).
 *    Terug en vooruit in de browser doen hetzelfde via popstate.
 *    Sinds 16 sep 2026 gaat de paginering (pagina 2, 3, ...) er ook
 *    doorheen. Die stond als gewone link in het raster, dus je kreeg een
 *    volledige herlaad en de browser zette je bovenaan de pagina, boven
 *    de kop en de filters (klacht klant: geen fijne shopbeleving). Nu
 *    wisselt alleen het raster en land je bovenaan de nieuwe rij
 *    producten, net onder de filterbalk.
 *
 * 2. Zwevende filterknop op telefoons: zodra de knop 'Filter' boven het
 *    raster uit beeld is gescrold, verschijnt linksonder een gouden knop
 *    die hetzelfde filterpaneel opent. Verdwijnt zodra een paneel of
 *    venster open is.
 *
 * Werkt zonder afhankelijkheden; bij twijfel (geen <main>, geen fetch,
 * andere host) valt alles terug op de gewone navigatie.
 */
(() => {
  const main = document.getElementById('MainContent');
  if (!main || !('fetch' in window) || !('DOMParser' in window)) return;

  const cache = new Map();
  const CACHE_MAX = 8;
  let bezig = false;

  const isCollectieLink = (a) => {
    if (!a || a.target === '_blank' || a.hasAttribute('download')) return false;
    let url;
    try {
      url = new URL(a.href, location.href);
    } catch (e) {
      return false;
    }
    if (url.origin !== location.origin) return false;
    if (!/^\/collections\//.test(url.pathname) && !/^\/search\/?$/.test(url.pathname)) return false;
    if (/\/products\//.test(url.pathname)) return false;
    return url;
  };

  const haal = (url) => {
    const sleutel = url.toString();
    if (cache.has(sleutel)) return cache.get(sleutel);
    const p = fetch(sleutel, { credentials: 'same-origin', headers: { 'X-Requested-With': 'erwal-zacht' } })
      .then((r) => {
        if (!r.ok) throw new Error('status ' + r.status);
        return r.text();
      })
      .catch((e) => {
        cache.delete(sleutel);
        throw e;
      });
    cache.set(sleutel, p);
    if (cache.size > CACHE_MAX) cache.delete(cache.keys().next().value);
    return p;
  };

  const voerScriptsUit = (wortel) => {
    wortel.querySelectorAll('script').forEach((oud) => {
      /* Scripts uit innerHTML draaien niet; opnieuw aanmaken wel. Modules
         met src die al geladen zijn, laadt de browser niet opnieuw. */
      const nieuw = document.createElement('script');
      [...oud.attributes].forEach((a) => nieuw.setAttribute(a.name, a.value));
      nieuw.textContent = oud.textContent;
      oud.replaceWith(nieuw);
    });
  };

  const scrollAnker = () => {
    const rij = main.querySelector('.ecp__row') || main.querySelector('.facets-controls-wrapper');
    return rij ? rij.getBoundingClientRect().top : null;
  };

  /* Let op: in Horizon scrollt het vénster niet. html en body staan op
     overflow hidden en het schuiven gebeurt in .page-wrapper. window.scrollTo
     en window.scrollBy doen hier dus helemaal niets (gemeten 16 sep 2026:
     window.scrollY blijft 0, document.scrollHeight is even hoog als het
     scherm). Alles hieronder werkt daarom op het echte scrollvak. */
  const scrollvak = () => {
    const kandidaat = main.closest('.page-wrapper') || document.querySelector('.page-wrapper');
    if (kandidaat) {
      const s = getComputedStyle(kandidaat);
      if ((s.overflowY === 'auto' || s.overflowY === 'scroll') && kandidaat.scrollHeight > kandidaat.clientHeight) {
        return kandidaat;
      }
    }
    return document.scrollingElement || document.documentElement;
  };

  /* Ruimte die bovenaan bezet is: de vastgeklikte kop, plus op telefoons de
     filter- en sorteerbalk die daar ook blijft plakken. */
  const vrijeRuimteBoven = () => {
    const kop = document.querySelector('.header-wrapper, .header, header');
    let hoog = kop ? kop.getBoundingClientRect().height : 0;
    const balk = main.querySelector('.facets-controls-wrapper');
    if (balk && getComputedStyle(balk).position === 'sticky') {
      hoog += balk.getBoundingClientRect().height;
    }
    return hoog;
  };

  const naarRaster = () => {
    const vak = scrollvak();
    /* Anker: op telefoons de knop Filter, zodat die na de wissel in beeld
       blijft (wens klant) — met het raster als anker landde hij op 15 px en
       dook hij onder de vastgeklikte kop van 60 px. Staat die knop er niet
       of is hij verborgen (desktop heeft de filters in de zijkolom), dan het
       raster zelf. De filterbalk is geen anker: die is op telefoons sticky
       en staat dus altijd op 0, waardoor er niets te rekenen viel. */
    const knop = main.querySelector('.facets-toggle__button');
    const zichtbaar = knop && knop.offsetParent !== null && knop.getBoundingClientRect().height > 0;
    const doel = zichtbaar
      ? knop
      : main.querySelector('#ResultsList') || main.querySelector('.product-grid');
    if (!doel) {
      vak.scrollTo({ top: 0, behavior: 'instant' });
      return;
    }
    const y = vak.scrollTop + doel.getBoundingClientRect().top - vrijeRuimteBoven() - 12;
    vak.scrollTo({ top: Math.max(0, y), behavior: 'instant' });
  };

  const wissel = async (url, { push = true, modus = 'pillen' } = {}) => {
    if (bezig) return;
    bezig = true;
    const ankerVoor = scrollAnker();
    main.classList.add('erwal-wissel');
    const sectie = main.querySelector('.shopify-section:has(.product-grid)');
    if (sectie) sectie.classList.add('erwal-laadt');
    try {
      const html = await haal(url);
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const nieuwMain = doc.getElementById('MainContent');
      if (!nieuwMain) throw new Error('geen main');
      document.title = doc.title;
      main.innerHTML = nieuwMain.innerHTML;
      voerScriptsUit(main);
      if (push) history.pushState({ erwalZacht: true }, '', url.toString());
      /* Scrollpositie: de pillenrij blijft waar hij stond; zonder pillen
         naar de bovenkant van de pagina. */
      if (modus === 'paginering') {
        naarRaster();
      } else {
        const ankerNa = scrollAnker();
        const vak = scrollvak();
        if (ankerVoor !== null && ankerNa !== null) {
          vak.scrollBy({ top: ankerNa - ankerVoor, behavior: 'instant' });
        } else {
          vak.scrollTo({ top: 0, behavior: 'instant' });
        }
      }
      zetZweefknop();
      document.dispatchEvent(new CustomEvent('erwal:zacht', { detail: { url: url.toString() } }));
    } catch (e) {
      /* Terugval: gewone navigatie. */
      location.href = url.toString();
      return;
    } finally {
      bezig = false;
      main.classList.remove('erwal-wissel');
    }
  };

  document.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a =
      e.target instanceof Element
        ? e.target.closest('a.ecp__pill, a.esub__link, .erwal-gekozen a.pills__pill, a.pagination__link')
        : null;
    if (!a || !main.contains(a)) return;
    const url = isCollectieLink(a);
    if (!url) return;
    e.preventDefault();
    wissel(url, { modus: a.classList.contains('pagination__link') ? 'paginering' : 'pillen' });
  });

  /* Vooraf ophalen bij aanraken of aanwijzen: de wissel voelt dan direct. */
  const prefetch = (e) => {
    const a = e.target instanceof Element ? e.target.closest('a.ecp__pill, a.esub__link, a.pagination__link') : null;
    if (!a) return;
    const url = isCollectieLink(a);
    if (url && url.href !== location.href) haal(url).catch(() => {});
  };
  document.addEventListener('pointerenter', prefetch, true);
  document.addEventListener('touchstart', prefetch, { passive: true, capture: true });

  window.addEventListener('popstate', (e) => {
    if (e.state && e.state.erwalZacht) {
      wissel(new URL(location.href), { push: false });
    } else if (history.state === null && /^\/(collections\/|search)/.test(location.pathname) && main.querySelector('.product-grid, .ecp__row')) {
      wissel(new URL(location.href), { push: false });
    }
  });
  if (/^\/(collections\/|search)/.test(location.pathname) && !history.state) {
    history.replaceState({ erwalZacht: true }, '', location.href);
  }

  /* ---- zoekveld boven de filters (snippets/erwal-zoekfilter.liquid) ----
     Het veld staat in Horizons filterformulier zonder name; Enter of de
     knop opent de zoekresultaten. Op collectie- en zoekpagina's zonder
     herlaad (zelfde wissel als de categorieën), elders gewoon navigeren.
     Invoer-events blijven bij het veld, anders ziet Horizon ze als een
     filterwissel. */
  const zoekUit = (veld) => {
    const term = (veld.value || '').trim();
    if (!term) {
      veld.focus();
      return;
    }
    const url = new URL('/search', location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('type', 'product');
    const paneel = veld.closest('dialog');
    if (paneel && typeof paneel.close === 'function') paneel.close();
    if (main.querySelector('.product-grid, .facets')) {
      wissel(url);
    } else {
      location.href = url.toString();
    }
  };
  document.addEventListener('keydown', (e) => {
    const veld = e.target instanceof Element ? e.target.closest('.erwal-zoekfilter__veld') : null;
    if (!veld) return;
    e.stopPropagation();
    if (e.key === 'Enter') {
      e.preventDefault();
      zoekUit(veld);
    }
  }, true);
  ['input', 'change'].forEach((type) => {
    document.addEventListener(type, (e) => {
      const veld = e.target instanceof Element ? e.target.closest('.erwal-zoekfilter__veld') : null;
      if (!veld) return;
      e.stopPropagation();
      if (type === 'input') zoekLive(veld);
    }, true);
  });

  /* ---- live resultaten tijdens het typen (wens klant, 6 sep 2026) ----
     Vanaf twee tekens vraagt het veld Shopify's suggest.json (dezelfde
     bron als de zoekbalk in de header) en toont maximaal zes producten
     onder het veld, met een regel naar alle resultaten. De lijst staat
     in de stroom (niet zwevend), zodat hij ook in het filterpaneel op
     telefoons niet wordt afgesneden. Pijltjes lopen door de lijst,
     Escape sluit, Enter blijft de volledige zoekpagina openen. */
  let zoekTimer = 0;
  let zoekCtrl = null;
  const zoekLijstVan = (veld) => veld.parentElement ? veld.parentElement.querySelector('.erwal-zoekfilter__lijst') : null;
  const zoekSluit = (veld) => {
    const lijst = zoekLijstVan(veld);
    if (lijst) {
      lijst.hidden = true;
      lijst.innerHTML = '';
    }
    veld.removeAttribute('aria-expanded');
  };
  const prijsTekst = (p) => {
    const raw = p && (p.price || (p.variants && p.variants[0] && p.variants[0].price));
    if (!raw) return '';
    const n = Number(String(raw).replace(',', '.'));
    if (!Number.isFinite(n)) return String(raw);
    return '\u20ac ' + n.toFixed(2).replace('.', ',');
  };
  const toonZoek = (veld, term, producten) => {
    const lijst = zoekLijstVan(veld);
    if (!lijst) return;
    lijst.innerHTML = '';
    if (!producten.length) {
      const leeg = document.createElement('p');
      leeg.className = 'erwal-zoekfilter__leeg';
      leeg.textContent = 'Geen producten gevonden voor \u2018' + term + '\u2019.';
      lijst.appendChild(leeg);
    }
    producten.forEach((p) => {
      const a = document.createElement('a');
      a.className = 'erwal-zoekfilter__item';
      a.href = p.url;
      const beeld = (p.featured_image && p.featured_image.url) || p.image || '';
      if (beeld) {
        const img = document.createElement('img');
        img.src = beeld.includes('?') ? beeld + '&width=120' : beeld + '?width=120';
        img.alt = '';
        img.width = 44;
        img.height = 44;
        img.loading = 'lazy';
        a.appendChild(img);
      }
      const tekst = document.createElement('span');
      tekst.className = 'erwal-zoekfilter__tekst';
      const titel = document.createElement('span');
      titel.className = 'erwal-zoekfilter__titel';
      titel.textContent = p.title || '';
      tekst.appendChild(titel);
      const prijs = prijsTekst(p);
      if (prijs) {
        const pr = document.createElement('span');
        pr.className = 'erwal-zoekfilter__prijs';
        pr.textContent = prijs;
        tekst.appendChild(pr);
      }
      a.appendChild(tekst);
      lijst.appendChild(a);
    });
    const alle = document.createElement('button');
    alle.type = 'button';
    alle.className = 'erwal-zoekfilter__alle';
    alle.textContent = 'Alle resultaten voor \u2018' + term + '\u2019';
    alle.addEventListener('click', () => zoekUit(veld));
    lijst.appendChild(alle);
    lijst.hidden = false;
    veld.setAttribute('aria-expanded', 'true');
  };
  const zoekLive = (veld) => {
    const term = (veld.value || '').trim();
    clearTimeout(zoekTimer);
    if (term.length < 2) {
      zoekSluit(veld);
      return;
    }
    zoekTimer = setTimeout(async () => {
      if (zoekCtrl) zoekCtrl.abort();
      zoekCtrl = new AbortController();
      const url = new URL('/search/suggest.json', location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '6');
      url.searchParams.set('resources[options][unavailable_products]', 'hide');
      url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag');
      try {
        const antwoord = await fetch(url.toString(), { signal: zoekCtrl.signal, headers: { Accept: 'application/json' } });
        const data = await antwoord.json();
        const producten = (data.resources && data.resources.results && data.resources.results.products) || [];
        if ((veld.value || '').trim() === term) toonZoek(veld, term, producten);
      } catch (fout) {
        if (fout && fout.name !== 'AbortError') zoekSluit(veld);
      }
    }, 220);
  };
  document.addEventListener('click', (e) => {
    if (!(e.target instanceof Element) || e.target.closest('.erwal-zoekfilter')) return;
    document.querySelectorAll('.erwal-zoekfilter__veld[aria-expanded]').forEach((veld) => zoekSluit(veld));
  });
  document.addEventListener('keydown', (e) => {
    if (!(e.target instanceof Element)) return;
    const vak = e.target.closest('.erwal-zoekfilter');
    if (!vak) return;
    const veld = vak.querySelector('.erwal-zoekfilter__veld');
    const items = [...vak.querySelectorAll('.erwal-zoekfilter__item, .erwal-zoekfilter__alle')];
    if (e.key === 'Escape') {
      if (veld) zoekSluit(veld);
      return;
    }
    if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && items.length) {
      e.preventDefault();
      const i = items.indexOf(document.activeElement);
      const volgende = e.key === 'ArrowDown' ? (i < 0 ? 0 : Math.min(i + 1, items.length - 1)) : (i <= 0 ? -1 : i - 1);
      if (volgende < 0) {
        if (veld) veld.focus();
      } else {
        items[volgende].focus();
      }
    }
  });
  document.addEventListener('click', (e) => {
    const knop = e.target instanceof Element ? e.target.closest('.erwal-zoekfilter__knop') : null;
    if (!knop) return;
    e.preventDefault();
    const veld = knop.parentElement.querySelector('.erwal-zoekfilter__veld');
    if (veld) zoekUit(veld);
  });

  /* ---- zwevende filterknop ---------------------------------------- */
  let knop = null;
  let waarnemer = null;

  const maakKnop = () => {
    if (knop) return knop;
    knop = document.createElement('button');
    knop.type = 'button';
    knop.className = 'erwal-zweeffilter';
    knop.setAttribute('aria-label', 'Filters openen');
    knop.innerHTML =
      '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M4 6h16M7 12h10M10 18h4"/></svg><span>Filter</span><span class="erwal-zweeffilter__tel" hidden>0</span>';
    knop.addEventListener('click', () => {
      const doel = main.querySelector('.facets-toggle__button');
      if (doel) doel.click();
    });
    document.body.appendChild(knop);
    return knop;
  };

  const zetZweefknop = () => {
    if (waarnemer) {
      waarnemer.disconnect();
      waarnemer = null;
    }
    const doel = main.querySelector('.facets-toggle__button');
    if (!doel || !('IntersectionObserver' in window)) {
      if (knop) knop.classList.remove('erwal-zweeffilter--aan');
      return;
    }
    const k = maakKnop();
    const tel = k.querySelector('.erwal-zweeffilter__tel');
    const aantal = doel.querySelector('.filter-count-bubble__text');
    const n = aantal ? parseInt(aantal.textContent, 10) || 0 : 0;
    tel.textContent = String(n);
    tel.hidden = n === 0;
    waarnemer = new IntersectionObserver(
      (items) => {
        const item = items[0];
        const voorbij = !item.isIntersecting && item.boundingClientRect.top < 0;
        k.classList.toggle('erwal-zweeffilter--aan', voorbij);
      },
      { threshold: 0 }
    );
    waarnemer.observe(doel);
  };

  /* ---- sorteerkeuze in de mobiele filterbalk ------------------------
     Horizon zet de sorteerkeuze op telefoons onderin het filterpaneel;
     de klant miste hem. Hier komt een gespiegelde keuze naast de knop
     'Filter'. Een wissel zet de waarde op de echte keuze in het paneel
     en vuurt daar 'change' af, waarna Horizon zelf via ajax sorteert. */
  /* Er zijn twee sorteerkeuzes in de DOM: die van de desktopbalk (op
     telefoons verborgen) en die in het filterpaneel. Alleen de laatste
     hoort bij het formulier dat Horizon op telefoons verstuurt, dus
     spiegelen we die. */
  const paneelSelect = () =>
    main.querySelector('.facets--drawer select.sorting-filter__select') ||
    main.querySelector('dialog select.sorting-filter__select') ||
    main.querySelector('select.sorting-filter__select');

  const zetSorteer = () => {
    const wrapper = main.querySelector('.facets-toggle__wrapper');
    const bron = paneelSelect();
    if (!wrapper || !bron) return;
    let sel = wrapper.querySelector('select.erwal-sorteer');
    if (!sel) {
      sel = document.createElement('select');
      sel.className = 'erwal-sorteer';
      sel.setAttribute('aria-label', 'Sorteren');
      sel.addEventListener('change', () => {
        const doel = paneelSelect();
        if (!doel) return;
        doel.value = sel.value;
        doel.dispatchEvent(new Event('change', { bubbles: true }));
      });
      wrapper.appendChild(sel);
    }
    const opties = [...bron.options].map((o) => o.value + '|' + o.textContent.trim()).join(';');
    if (sel.dataset.opties !== opties) {
      sel.innerHTML = '';
      [...bron.options].forEach((o) => {
        const n = document.createElement('option');
        n.value = o.value;
        n.textContent = o.textContent.trim().replace(/^Sorteren:?\s*/i, '');
        sel.appendChild(n);
      });
      sel.dataset.opties = opties;
    }
    sel.value = bron.value;
  };

  zetZweefknop();
  zetSorteer();

  /* Horizon vervangt bij het ajax-filteren de hele productsectie, dus
     ook de knop 'Filter' die we in de gaten houden en de sorteerkeuze.
     Zodra die knop een ander element is geworden, opnieuw koppelen
     (met een kleine pauze tegen het hameren tijdens de wissel). */
  let huidigeDoel = main.querySelector('.facets-toggle__button');
  let timer = null;
  new MutationObserver(() => {
    if (timer) return;
    timer = setTimeout(() => {
      timer = null;
      const doel = main.querySelector('.facets-toggle__button');
      if (doel !== huidigeDoel) {
        huidigeDoel = doel;
        zetZweefknop();
      }
      zetSorteer();
    }, 150);
  }).observe(main, { childList: true, subtree: true });
})();

/* 3. Aantalkiezer op de productkaart (6 sep 2026): min en plus zetten het
 *    veld quantity in het kaartformulier; het formulier stuurt dat mee.
 *    Capture en stopPropagation, zodat de klik niet doorloopt naar de
 *    kaartlink of Horizons kaartlogica. Los van het blok hierboven, dat
 *    op pagina's zonder #MainContent vroeg stopt. */
(() => {
  document.addEventListener('click', (e) => {
    const knop = e.target instanceof Element ? e.target.closest('.erwal-stepper__knop') : null;
    if (!knop) return;
    e.preventDefault();
    e.stopPropagation();
    const veld = knop.parentElement && knop.parentElement.querySelector('.erwal-stepper__veld');
    if (!veld) return;
    const min = parseInt(veld.min || '1', 10) || 1;
    const max = parseInt(veld.max || '99', 10) || 99;
    const stap = parseInt(knop.dataset.stap || '1', 10) || 1;
    const nu = parseInt(veld.value, 10) || min;
    veld.value = String(Math.max(min, Math.min(max, nu + stap)));
  }, true);
  document.addEventListener('click', (e) => {
    const veld = e.target instanceof Element ? e.target.closest('.erwal-stepper__veld') : null;
    if (veld) e.stopPropagation();
  }, true);
})();

